import { animate, style, transition, trigger } from '@angular/animations';

import { SEMANTIC_TOKENS } from 'src/app/core/theming/theme-token-catalog';
import { duration_isValid } from './motion-duration';

// Uitklappaneel (ExpansionListComponent, NavigationChecklistComponent): hoogte 0 ⇄ auto, met fade.
// Padding en bovenrand van het paneel (.expansion-row__panel) gaan mee naar 0: alleen height op 0 laat die
// ~13px staan, waardoor het paneel bij openen eerst verspringt en bij sluiten op het eind ineens wegvalt.
// Openen en sluiten even lang, zodat wisselen tussen twee rijen (de ene sluit, de andere opent) gelijk oploopt.
//
// Duur en verloop zijn thema-tokens (--shape-expansion-list-duration/-easing, AdminPanel › Theme › Elements).
// Angular-animaties lezen geen CSS-variabelen: de lijst leest ze met expandPanel_read bij elk open-/dichtklappen
// en bindt ze als params: [@expandPanel]="expandPanel$$()".
const __collapsed = { height: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0, opacity: 0 };

// Terugval uit de catalogus (SSOT), niet een eigen kopie. De duur volgt standaard de gedeelde animatieduur uit
// Layout (--layout-motion-duration); de letterlijke laatste terugval is daarom diens catalogus-standaard.
const __durationToken = SEMANTIC_TOKENS['shape.expansion-list.duration'];
const __easingToken = SEMANTIC_TOKENS['shape.expansion-list.easing'];
const __motionDurationToken = SEMANTIC_TOKENS['layout.motion.duration'];

export const EXPAND_PANEL_DEFAULT_TIMING = `${__motionDurationToken.default} ${__easingToken.default}`;

const __duration_isValid = duration_isValid;

export type ExpandPanelBinding = { value: true; params: { timing: string } };

export const expandPanelAnimation = trigger('expandPanel', [
    transition(':enter', [
        style({ ...__collapsed, overflow: 'hidden' }),
        animate('{{timing}}', style({ height: '*', paddingTop: '*', paddingBottom: '*', borderTopWidth: '*', opacity: 1 })),
    ], { params: { timing: EXPAND_PANEL_DEFAULT_TIMING } }),
    transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('{{timing}}', style(__collapsed)),
    ], { params: { timing: EXPAND_PANEL_DEFAULT_TIMING } }),
]);

export function expandPanel_binding(timing: string = EXPAND_PANEL_DEFAULT_TIMING): ExpandPanelBinding {
    return { value: true, params: { timing } };
}

// Leest de thema-tokens op `element` (geldt ook voor een thema-voorvertoning op een voorouder). Een ongeldige
// waarde (geen ms/s-duur, of een verloop dat de browser niet kent) valt per onderdeel terug op de standaard.
export function expandPanel_read(element: Element | null | undefined): ExpandPanelBinding {
    if (!element) return expandPanel_binding();

    const __style = getComputedStyle(element);
    // Custom properties geven hun var()-verwijzingen al opgelost terug: de standaard van de lijst-duur levert
    // dus direct de (eventueel in het thema aangepaste) Layout-duur op.
    const __duration = __style.getPropertyValue(__durationToken.cssVar).trim();
    const __motionDuration = __style.getPropertyValue(__motionDurationToken.cssVar).trim();
    const __easing = __style.getPropertyValue(__easingToken.cssVar).trim();

    // Eigen duur → gedeelde Layout-duur → catalogus-standaard van Layout.
    const __durationUsed = __duration_isValid(__duration) ? __duration
        : __duration_isValid(__motionDuration) ? __motionDuration
        : __motionDurationToken.default;
    const __easingOk = !!__easing && !/[{}]/.test(__easing) && CSS.supports('transition-timing-function', __easing);

    return expandPanel_binding(`${__durationUsed} ${__easingOk ? __easing : __easingToken.default}`);
}
